import React, { useState, useCallback, useMemo, useRef } from 'react';

import omit from 'lodash/omit';

import { useTranslate } from '@lumapps/translations';

import type { NavigationItems, NavigationPage, NavigationPageText } from '../../types';

const getPageTextId = (page: NavigationPage, textIndex: number): string => `${page.id}_${textIndex}`;

/**
 * Stable context: registration functions only.
 * Never changes when items change.
 */
export interface SpotlightActionsContextValue {
    registerPage: (page: NavigationPage) => void;
    unregisterPage: (id: string) => void;
}

/**
 * Data context: the registered navigation items.
 * Changes on every registerPage / unregisterPage call.
 */
export interface SpotlightItemsContextValue {
    items: NavigationItems;
}

/**
 * This context uses the "context splitting" pattern to avoid unnecessary re-renders.
 *
 * The naive approach — a single context with both `items` and `registerPage` — causes every
 * consumer to re-render whenever a page is registered, even those that only call `registerPage`
 * and never read `items`. This is because `useContext` triggers a re-render on every change to
 * the context value, regardless of which property the consumer actually uses.
 *
 * Splitting into two contexts solves this:
 *  - SpotlightActionsContext: holds `registerPage` and `unregisterPage`. These are stable
 *    callbacks (memoized with useCallback) and only change if `translateKey` changes — which
 *    almost never happens. Consumers (e.g. nav menu items) that only register pages subscribe
 *    to this context and are never re-rendered when the item list changes.
 *  - SpotlightItemsContext: holds `items`. This changes on every register/unregister. Only
 *    the Spotlight component, which actually displays the results, subscribes to it.
 *
 * Hooks: use `useSpotlightActions` to access actions, `useSpotlightItems` to access data.
 */
export const SpotlightActionsContext = React.createContext<SpotlightActionsContextValue | null>(null);
export const SpotlightItemsContext = React.createContext<SpotlightItemsContextValue | null>(null);
export const SpotlightProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { translateKey } = useTranslate();
    const [pages, setPages] = useState<Record<string, NavigationPage>>({});
    const [pageTexts, setPageTexts] = useState<Record<string, NavigationPageText>>({});

    /**
     * Reference counter tracking how many menu levels have registered each page ID.
     * This solves the nested-menu case: when a parent menu (level N) pre-registers a
     * child route via extractRoutes, and a child menu (level N+1) also registers the
     * same ID when it mounts, the count reaches 2. When the child menu unmounts and
     * calls unregisterPage, the count drops to 1 — the page stays in Spotlight because
     * the parent still holds a registration. The page is only removed when all
     * registrants have released their slot (count → 0).
     */
    const registrationCounts = useRef<Record<string, number>>({});

    const registerPage = useCallback(
        (page: NavigationPage) => {
            registrationCounts.current[page.id] = (registrationCounts.current[page.id] ?? 0) + 1;
            setPages((prev) => ({ ...prev, [page.id]: page }));

            if (page.pageKeys?.length) {
                const parentPage = omit(page, 'pageKeys');
                setPageTexts((prev) =>
                    (page.pageKeys as string[]).reduce((acc, curr, index) => {
                        const pageTextId = getPageTextId(page, index);
                        const pageText: NavigationPageText = {
                            id: pageTextId,
                            label: translateKey(curr),
                            parentPage,
                        };

                        return { ...acc, [pageTextId]: pageText };
                    }, prev),
                );
            }
        },
        [translateKey],
    );

    const unregisterPage = useCallback((id: string) => {
        const count = registrationCounts.current[id] ?? 0;

        if (count > 1) {
            // Other menu levels still hold a registration — just decrement, keep the page.
            registrationCounts.current[id] = count - 1;
            return;
        }

        // Last registration released: remove from spotlight.
        delete registrationCounts.current[id];
        setPages((prev) => omit(prev, id));

        /**
         * Remove all texts that belong to the unregistered page.
         * Each NavigationPageText stores a reference to its parent page via `parentPage.id`,
         * so we can filter them out without needing to read from the `pages` state.
         */
        setPageTexts((prev) =>
            Object.entries(prev).reduce<Record<string, NavigationPageText>>((acc, [key, pageText]) => {
                // filter pageTexts that have the page to unregister as parentPage
                if (pageText.parentPage.id !== id) {
                    acc[key] = pageText;
                }

                return acc;
            }, {}),
        );
    }, []);

    // Stable value: only recreated if registerPage or unregisterPage change (i.e. almost never).
    // Subscribers to this context (nav menu items) are not re-rendered when items change.
    const actionsValue = useMemo(() => ({ registerPage, unregisterPage }), [registerPage, unregisterPage]);

    // Volatile value: recreated on every register/unregister.
    // Only the Spotlight component subscribes to this context.
    const itemsValue = useMemo(
        () => ({
            items: {
                pages: Object.values(pages),
                texts: Object.values(pageTexts),
            },
        }),
        [pages, pageTexts],
    );

    return (
        <SpotlightActionsContext.Provider value={actionsValue}>
            <SpotlightItemsContext.Provider value={itemsValue}>{children}</SpotlightItemsContext.Provider>
        </SpotlightActionsContext.Provider>
    );
};
